// Added by Sejune Oh on 2019-09-02
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { makeAutoObservable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Util
import { getQueryPath } from '@/helper/util'
import Intl from '@/helper/Intl'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Root Store
import RootStore from '../../index'
import {
  reqGetGateways,
  reqGetGateway,
  reqCreateGateway,
  reqUpdateGateway,
  reqDeleteGateway,
} from './GatewayStore.service'
import { ElementTimeStampProps } from '@/helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export interface GatewayElementProps extends ElementTimeStampProps {
  id?: string
  fk_group_id?: string
  fk_data_storage_id?: string
  gateway_token?: string
  name?: string
  activated?: boolean
  hardware_key?: string
  description?: string
  connected?: boolean
  ip_address?: string
  version?: string
  os_name?: string
  connected_at?: string
}

export interface DisplableGatewayElementProps extends GatewayElementProps {
  txt_activated?: string
  txt_connected?: string
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class GatewayStore {
  _totalCount: number = 0
  _elements: Array<GatewayElementProps | DisplableGatewayElementProps> = []

  root?: RootStore

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  constructor(root: RootStore) {
    this.root = root
    makeAutoObservable(this)
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  get elements() {
    return this._elements
  }

  get totalCount() {
    return this._totalCount
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _setTotalCount = (count: number) => {
    this._totalCount = count
  }

  setElements = (elements: Array<GatewayElementProps>) => {
    this._elements = elements
  }

  setDisplaybleElements = (elements: Array<GatewayElementProps>) => {
    const trueTxt = Intl.getIntlText({ id: 'htmljs_react.global.gatewaystore.text_true' })
    const falseTxt = Intl.getIntlText({ id: 'htmljs_react.global.gatewaystore.text_false' })
    this._elements = elements.map((element) => ({
      ...element,
      txt_activated: element.activated ? trueTxt : falseTxt,
      txt_connected: element.connected ? trueTxt : falseTxt,
      ip_address: element.ip_address !== '' ? element.ip_address : '-',
    }))
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getElements = async (path: string) => {
    const {
      data: { total_count, items },
    } = await reqGetGateways(path)
    this._setTotalCount(parseInt(total_count!))
    return items!
  }

  getElement = async (id: string) => {
    const { data: rtnElement } = await reqGetGateway(id)
    return rtnElement
  }

  addElement = async (element: GatewayElementProps) => {
    const { data: rtnElement } = await reqCreateGateway(element)
    return rtnElement
  }

  updateElement = async (id: string, element: GatewayElementProps) => {
    const { data: updatedElement } = await reqUpdateGateway(id, element)
    return updatedElement.id
  }

  removeElement = async (id: string) => {
    return await reqDeleteGateway(id)
  }

  checkDuplicatedGatewayName = async (name: string) => {
    const filter = { $op: 'and', name: name }
    const path = getQueryPath({ pageIndex: 0, orderBy: 'name', filter: JSON.stringify(filter) })

    const elements = await this.getElements(path)
    return elements.length > 0
  }

  resetHWKey = async (id: string, element: GatewayElementProps) => {
    const { data: updatedElement } = await reqUpdateGateway(id, {
      ...element,
      hardware_key: '',
    })
    return updatedElement.id
  }
}
